import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { Product } from '../../types';
import { trackFacebookEvent } from '../../utils/facebookPixel';
import { getImageUrl } from '../../utils/imageUrl';

interface QuickOrderModalProps {
  product: Product;
  onClose: () => void;
}

const QuickOrderModal: React.FC<QuickOrderModalProps> = ({ product, onClose }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  // Selection state lives here — isolated from ProductCard
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedWeight, setSelectedWeight] = useState('');

  const primaryImage = getImageUrl(
    product.image ||
    product.primary_image ||
    product.images?.find(img => img.is_primary)?.image ||
    product.images?.[0]?.image
  );

  const hasColors = !!(product.attributes?.colors?.enabled && (product.attributes.colors.options || []).length > 0);
  const hasSizes = !!(product.attributes?.sizes?.enabled && (product.attributes.sizes.options || []).length > 0);
  const hasWeights = !!(product.attributes?.weights?.enabled && (product.attributes.weights.options || []).length > 0);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [missingAttributes, setMissingAttributes] = useState<{ color?: boolean; size?: boolean; weight?: boolean }>({});

  const handleConfirm = () => {
    const missingColor = hasColors && !selectedColor;
    const missingSize = hasSizes && !selectedSize;
    const missingWeight = hasWeights && !selectedWeight;

    if (missingColor || missingSize || missingWeight) {
      setMissingAttributes({ color: missingColor, size: missingSize, weight: missingWeight });
      return;
    }

    const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
    trackFacebookEvent('AddToCart', {
      content_name: productName,
      content_ids: [product.id.toString()],
      content_type: 'product',
      value: product.final_price,
      currency: 'BDT',
    });
    const orderData = {
      product,
      quantity: 1,
      selected_color: selectedColor,
      selected_size: selectedSize,
      selected_weight: selectedWeight,
    };
    sessionStorage.setItem('directOrder', JSON.stringify(orderData));
    onClose();
    navigate('/checkout');
  };

  const modal = (
    <div className="quick-order-overlay" onClick={onClose}>
      <div className="quick-order-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="qo-modal-header">
          <span className="qo-modal-title">
            {currentLang === 'bn' ? 'অর্ডার করুন' : 'Place Order'}
          </span>
          <button className="qo-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Product brief */}
        <div className="qo-modal-product">
          <img
            src={primaryImage}
            alt={currentLang === 'bn' ? product.name_bn : product.name_en}
            className="qo-modal-thumb"
          />
          <div className="qo-modal-product-info">
            <p className="qo-modal-product-name">
              {currentLang === 'bn' ? product.name_bn : product.name_en}
            </p>
            <p className="qo-modal-product-price">
              {product.discount_price
                ? <>
                    <span className="qo-price">৳{product.discount_price.toLocaleString()}</span>
                    <span className="qo-original-price">৳{product.price.toLocaleString()}</span>
                  </>
                : <span className="qo-price">৳{product.price.toLocaleString()}</span>
              }
            </p>
          </div>
        </div>

        {/* Scrollable attribute area — only render if at least one attribute has options */}
        {(hasColors || hasSizes || hasWeights) && (
        <div className="qo-modal-attrs">
          {/* Color */}
          {hasColors && (
            <div className={`qo-attr-group ${missingAttributes.color ? 'attr-error' : ''}`}>
              <span className="qo-attr-label">
                {currentLang === 'bn' ? 'রং' : 'Color'}
                {selectedColor ? (
                  <span className="qo-attr-selected"> — {selectedColor}</span>
                ) : missingAttributes.color ? (
                  <span className="attr-required-badge" style={{ marginLeft: 'auto', fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>{currentLang === 'bn' ? '* আবশ্যক' : '* Required'}</span>
                ) : null}
              </span>
              <div className="qo-color-swatches">
                {(product.attributes!.colors!.options || []).map(color => (
                  <button
                    key={color}
                    type="button"
                    title={color}
                    className={`qo-color-swatch ${selectedColor === color ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedColor(prev => prev === color ? '' : color);
                      setMissingAttributes(prev => ({ ...prev, color: false }));
                    }}
                  >
                    <span className="qo-color-dot" style={{ background: color.toLowerCase() }} />
                    <span className="qo-color-name">{color}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          {hasSizes && (
            <div className={`qo-attr-group ${missingAttributes.size ? 'attr-error' : ''}`}>
              <span className="qo-attr-label">
                {currentLang === 'bn' ? 'সাইজ' : 'Size'}
                {selectedSize ? (
                  <span className="qo-attr-selected"> — {selectedSize}</span>
                ) : missingAttributes.size ? (
                  <span className="attr-required-badge" style={{ marginLeft: 'auto', fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>{currentLang === 'bn' ? '* আবশ্যক' : '* Required'}</span>
                ) : null}
              </span>
              <div className="qo-chips-row">
                {(product.attributes!.sizes!.options || []).map(size => (
                  <button
                    key={size}
                    type="button"
                    className={`qo-chip ${selectedSize === size ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedSize(prev => prev === size ? '' : size);
                      setMissingAttributes(prev => ({ ...prev, size: false }));
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Weight */}
          {hasWeights && (
            <div className="qo-attr-group">
              <span className="qo-attr-label">
                {currentLang === 'bn' ? 'ওজন' : 'Weight'}
                {selectedWeight && <span className="qo-attr-selected"> — {selectedWeight}</span>}
              </span>
              <div className="qo-chips-row">
                {(product.attributes!.weights!.options || []).map(weight => (
                  <button
                    key={weight}
                    type="button"
                    className={`qo-chip ${selectedWeight === weight ? 'selected' : ''}`}
                    onClick={() => setSelectedWeight(prev => prev === weight ? '' : weight)}
                  >
                    {weight}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Confirm button — always pinned at bottom */}
        <div className="qo-confirm-area">
          <button className="qo-confirm-btn" onClick={handleConfirm}>
            {currentLang === 'bn' ? 'অর্ডার করুন' : 'Place Order'}
          </button>
        </div>

      </div>
    </div>
  );

  // Portal: render outside the card DOM to avoid stacking-context issues
  return ReactDOM.createPortal(modal, document.body);
};

export default QuickOrderModal;
